import { useState } from 'react';
import { Button, IconWifiSlash } from '@7shifts/sous-chef-base';
import { TopBar } from './TopBar';
import { AppFooter } from './AppFooter';
import { StartShiftIcon } from './UserStatusPage/icons/StartShiftIcon';
import { useTranslation } from '@src/i18n';
import './OfflineIntermediatePage.css';

interface OfflineIntermediatePageProps {
    onConfirm: () => void | Promise<void>;
    onCancel: () => void;
}

/**
 * Intermediate page shown after a PIN is entered while offline (API-4380 PR
 * B), before handing off to the existing offline dispatch (role selection /
 * stored / etc.). The profile hasn't been resolved yet, so the greeting stays
 * generic — no name or avatar. Layout mirrors the online UserStatusPage.
 */
export function OfflineIntermediatePage({
    onConfirm,
    onCancel
}: OfflineIntermediatePageProps) {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);

    async function handleClick() {
        if (submitting) return;
        setSubmitting(true);
        try {
            await onConfirm();
        } catch {
            // Swallow so the page stays available for another attempt rather
            // than surfacing an unhandled rejection.
        } finally {
            // Reset so the page never gets stuck loading if onConfirm rejects
            // or resolves without navigating away, keeping retry available.
            setSubmitting(false);
        }
    }

    return (
        <div
            data-testid="offline-intermediate-page"
            className="offline-intermediate-page"
        >
            <TopBar statusMode onStatusDismiss={onCancel} />
            <div className="offline-intermediate-page__content">
                <div className="offline-intermediate-page__left-panel">
                    <div className="offline-intermediate-page__greeting">
                        <h1 className="offline-intermediate-page__greeting-title">
                            {t('offline.intermediate.greeting')}
                        </h1>
                        <p className="offline-intermediate-page__greeting-subtitle">
                            {t('offline.intermediate.haveAGreatShift')}
                        </p>
                    </div>
                </div>
                <div className="offline-intermediate-page__right-panel">
                    <div className="offline-intermediate-page__panel">
                        <div className="offline-intermediate-page__heading">
                            <IconWifiSlash size="default" />
                            <h1 className="offline-intermediate-page__title">
                                {t('offline.intermediate.heading')}
                            </h1>
                        </div>
                        <div className="offline-intermediate-page__card-box">
                            <p className="offline-intermediate-page__body">
                                {t('offline.intermediate.body')}
                            </p>
                            <Button
                                theme="primary"
                                size="full-width"
                                loading={submitting}
                                onClick={handleClick}
                            >
                                {t('offline.intermediate.punchButton')}
                                <StartShiftIcon />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <AppFooter />
        </div>
    );
}
